import React, { useState, useEffect } from 'react';
import { FiEdit2, FiTrash2, FiPlus, FiX, FiCheck, FiAlertCircle } from 'react-icons/fi';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { Tag, createTag, getTags, updateTag, deleteTag } from '../services/LabelService';
import { ConfirmModal, FormModal } from '../components/SharedModal';
import { getSectors, Sector } from '../services/SectorService';
import SessionService from '../services/SessionService';
import { useSector } from '../contexts/SectorContext';
import './LabelScreen.css';
import Toast, { ToastContainer } from '../components/Toast';

// Extended color palette with 54 colors
const PRESET_COLORS = [
  // Reds
  '#FF6B6B', '#FF0000', '#FF4500', '#DC143C', '#B22222', '#8B0000',
  // Pinks/Purples
  '#D4A5A5', '#FF69B4', '#FF1493', '#C71585', '#9B59B6', '#8A2BE2',
  '#9370DB', '#6A5ACD', '#483D8B', '#4B0082',
  // Blues
  '#45B7D1', '#1E90FF', '#00BFFF', '#87CEEB', '#3498DB', '#0000FF',
  '#0000CD', '#00008B', '#191970',
  // Greens
  '#96CEB4', '#2ECC71', '#00FF7F', '#3CB371', '#2E8B57', '#006400',
  '#00FF00', '#32CD32', '#8FBC8F', '#1ABC9C',
  // Yellows/Oranges
  '#FFEEAD', '#F1C40F', '#FFD700', '#FFA500', '#FF8C00', '#E67E22',
  // Neutrals
  '#7F8C8D', '#A9A9A9', '#808080', '#696969', '#2F4F4F', '#000000',
  // Teals/Cyans
  '#00ADB5', '#008B8B', '#008080', '#20B2AA', '#5F9EA0', '#00CED1',
  // Browns
  '#CD853F', '#D2691E', '#8B4513', '#A0522D', '#800000'
];

interface ToastMessage {
  id: number;
  message: string;
  type: 'success' | 'error' | 'info';
}

const LabelScreen = () => {
  // Conexão do contexto (a mesma do menu): na carga direta da página ela chega depois do primeiro render.
  const { selectedSectorId: contextSectorId } = useSector();
  const [tags, setTags] = useState<Tag[]>([]);
  const [sectors, setSectors] = useState<Sector[]>([]);
  const [editing, setEditing] = useState<number | null>(null);
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [selectedSectorId, setSelectedSectorId] = useState<number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [errors, setErrors] = useState<{
    title?: string;
    description?: string;
  }>({});
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const [selectedColor, setSelectedColor] = useState<string>(PRESET_COLORS[0]);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState<number | null>(null);
  const [isDeletingTag, setIsDeletingTag] = useState(false);
  const [isSavingTag, setIsSavingTag] = useState(false);

  useEffect(() => {
    const sectorId = contextSectorId;
    if (sectorId) {
      fetchTags();
      fetchSectors();
    }
  }, [contextSectorId]);

  useEffect(() => {
    console.log('showModal state changed to:', showModal);
  }, [showModal]);

  const fetchTags = async () => {
    const sectorId = contextSectorId;
    if (!sectorId) return;

    try {
      setIsLoading(true);
      const response = await getTags(sectorId);
      setTags(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erro ao buscar etiquetas:', error);
      setTags([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSectors = async () => {
    const token = SessionService.getSession('authToken');
    if (!token) return;

    try {
      const response = await getSectors(token);
      setSectors(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Erro ao buscar setores:', error);
      setSectors([]);
    }
  };

  const handleEdit = (index: number) => {
    setEditing(index);
    setNewTitle(tags[index].name);
    setNewDescription(tags[index].description);
    setSelectedSectorId(tags[index].sectorId);
    setSelectedColor(tags[index].color || PRESET_COLORS[0]);
    setShowModal(true);
  };

  const validateForm = () => {
    const newErrors: { title?: string; description?: string } = {};

    if (!newTitle.trim()) {
      newErrors.title = 'O nome da etiqueta é obrigatório';
    } else if (newTitle.length < 3) {
      newErrors.title = 'O nome deve ter pelo menos 3 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const addToast = (message: string, type: 'success' | 'error' | 'info') => {
    const newToast = {
      id: Date.now(),
      message,
      type,
    };
    setToasts(current => [...current, newToast]);
  };

  const removeToast = (id: number) => {
    setToasts(current => current.filter(toast => toast.id !== id));
  };

  const handleSave = async () => {
    setIsSavingTag(true);
    if (!validateForm()) {
      setIsSavingTag(false);
      return;
    }

    const sectorId = contextSectorId;
    if (!sectorId) {
      addToast('Selecione uma conexão para continuar', 'error');
      setIsSavingTag(false);
      return;
    }

    try {
      const tagData = {
        name: newTitle,
        description: newDescription,
        sectorId: sectorId,
        color: selectedColor
      };

      if (editing !== null) {
        if (editing < tags.length) {
          await updateTag(tags[editing].id, tagData);
          addToast('Etiqueta atualizada.', 'success');
        }
      } else {
        await createTag(tagData);
        addToast('Etiqueta criada.', 'success');
      }

      await fetchTags();
      handleCloseModal();
    } catch (error) {
      console.error('Erro ao salvar etiqueta:', error);
      setErrors({ title: 'Não foi possível salvar a etiqueta. Tente novamente.' });
      addToast('Não foi possível salvar a etiqueta', 'error');
    }
    setIsSavingTag(false);
  };

  const handleDelete = async (index: number) => {
    setIsDeletingTag(true);
    try {
      await deleteTag(tags[index].id);
      await fetchTags();
      setDeleteModalOpen(null);
      addToast('Etiqueta excluída.', 'success');
    } catch (error) {
      console.error('Erro ao excluir etiqueta:', error);
      addToast('Não foi possível excluir a etiqueta', 'error');
    }
    setIsDeletingTag(false);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditing(null);
    setNewTitle('');
    setNewDescription('');
    setErrors({});
  };

  const handleAddNew = () => {
    setEditing(null);
    setNewTitle('');
    setNewDescription('');
    setSelectedColor(PRESET_COLORS[0]);
    setShowColorPicker(false);
    setShowModal(true);
  };

  return (
    <div className="label-screen-wrapper">
      <div className="ls-container">
        <div className="ls-header">
          <div className="ls-header-content">
            <h1>Etiquetas</h1>
            <p className="ls-header-description">Organize seus contatos com etiquetas</p>
          </div>
          <button
            className="ls-add-button"
            onClick={handleAddNew}
            disabled={!contextSectorId}
          >
            <FiPlus />
            Nova etiqueta
          </button>
        </div>

        <div className="ls-content">
          {isLoading && contextSectorId ? (
            <div className="ls-loading">
              <LoadingSpinner size="lg" color="primary" />
              <span className="ls-loading-text">Carregando etiquetas...</span>
            </div>
          ) : !contextSectorId ? (
            <div className="ls-no-sector">
              <h3>Nenhuma conexão selecionada</h3>
              <p>Selecione uma conexão para ver as etiquetas</p>
            </div>
          ) : tags.length === 0 ? (
            <div className="ls-empty">
              <div className="ls-empty-icon">
                <FiAlertCircle />
              </div>
              <h3 className="ls-empty-title">Nenhuma etiqueta encontrada</h3>
              <p className="ls-empty-description">Clique em "Nova etiqueta" para começar</p>
            </div>
          ) : (
            <div className="ls-table">
              <div className="ls-row ls-row-header">
                <div className="ls-name">Nome</div>
                <div className="ls-description">Descrição</div>
                <div className="ls-actions">Ações</div>
              </div>
              {tags.map((tag, index) => (
                <div key={tag.id} className="ls-row">
                  <div className="ls-name">
                    <span
                      className="ls-color"
                      style={{ backgroundColor: tag.color }}
                    ></span>
                    {tag.name}
                  </div>
                  <div className="ls-description">
                    {tag.description || "Sem descrição"}
                  </div>
                  <div className="ls-actions">
                    <button
                      className="ls-action-button edit"
                      onClick={() => handleEdit(index)}
                      title="Editar"
                    >
                      <FiEdit2 />
                    </button>
                    <button
                      className="ls-action-button delete"
                      onClick={() => setDeleteModalOpen(index)}
                      title="Excluir"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <FormModal
        open={showModal}
        onClose={handleCloseModal}
        onSubmit={handleSave}
        title={editing !== null && editing < tags.length ? 'Editar etiqueta' : 'Nova etiqueta'}
        submitLabel="Salvar"
        cancelLabel="Cancelar"
        loading={isSavingTag}
        submitDisabled={!newTitle.trim()}
      >
        <div className="shared-modal__form-group">
          <label htmlFor="label-name">Nome <span className="required">*</span></label>
          <input
            id="label-name"
            type="text"
            value={newTitle}
            onChange={(e) => {
              setNewTitle(e.target.value);
              if (errors.title) setErrors(prev => ({ ...prev, title: undefined }));
            }}
            placeholder="Nome da etiqueta"
            className={errors.title ? 'error' : ''}
          />
          {errors.title && <span className="shared-modal__error">{errors.title}</span>}
        </div>
        <div className="shared-modal__form-group">
          <label>Descrição</label>
          <textarea
            value={newDescription}
            onChange={(e) => {
              setNewDescription(e.target.value);
              if (errors.description) setErrors(prev => ({ ...prev, description: undefined }));
            }}
            placeholder="Descrição (opcional)"
            className={errors.description ? 'error' : ''}
          />
          <span className="label-char-count">{newDescription.length}/200 caracteres</span>
        </div>
        <div className="shared-modal__form-group">
          <label>Cor da etiqueta</label>
          <div className="ls-color-picker">
            <div
              className="ls-color-preview"
              style={{ backgroundColor: selectedColor }}
              onClick={() => setShowColorPicker(!showColorPicker)}
            />
            {showColorPicker && (
              <div className="ls-color-grid">
                {PRESET_COLORS.map((color) => (
                  <div
                    key={color}
                    className={`ls-color-option ${selectedColor === color ? 'selected' : ''}`}
                    style={{ backgroundColor: color }}
                    onClick={() => {
                      setSelectedColor(color);
                      setShowColorPicker(false);
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </FormModal>

      <ConfirmModal
        open={deleteModalOpen !== null}
        onClose={() => { if (!isDeletingTag) setDeleteModalOpen(null); }}
        onConfirm={() => { if (deleteModalOpen !== null && !isDeletingTag) handleDelete(deleteModalOpen); }}
        title="Confirmar exclusão"
        message={deleteModalOpen !== null ? `Excluir a etiqueta "${tags[deleteModalOpen].name}"?` : ''}
        confirmLabel="Excluir"
        cancelLabel="Cancelar"
        variant="danger"
        loading={isDeletingTag}
      />

      <ToastContainer>
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </ToastContainer>
    </div>
  );
};

export default LabelScreen;
